/* global mainScript */
import React from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { toast } from "react-toastify";

// style rules
import { Animation } from "../../styles";

const showToast = (step, message, type = "info") => {
	const content = (
		<p>
			<b>Step {step}:</b> {message}
		</p>
	);

	toast[type](content, {
		autoClose: false,
		closeOnClick: true,
	});
};

const MediaCard = ({ resource }) => {
	const fileDownload = resource.image;
	
	const copyToClipboard = (html, platform) => {
		if (!html) return;
	
		// Create a temporary div to parse the HTML
		const tempDiv = document.createElement("div");
		tempDiv.innerHTML = html;
	
		// Function to extract text while keeping <a> and wrapping block quotes in ""
		const extractText = (node) => {
			if (node.nodeType === Node.TEXT_NODE) {
				return node.textContent;
			} else if (node.nodeType === Node.ELEMENT_NODE) {
				if (node.tagName === "A") {
					return `${node.textContent}: ${node.href}`;
				} else if (node.tagName === "BLOCKQUOTE") {
					let text = Array.from(node.childNodes).map(extractText).join("");
					return `"${text.trim()}"`;
				} else {
					return Array.from(node.childNodes).map(extractText).join("");
				}
			}
			return "";
		};
	
		// Get the cleaned-up text
		let cleanText = extractText(tempDiv);
	
		// Determine additional text based on the platform
		let appendedText = "";
		switch (platform) {
				case "Facebook":
					appendedText = " AmericanRivers";
					break;
				case "LinkedIn":
					appendedText = " @American Rivers";
					break;
				case "Instagram":
				case "X":
					appendedText = " @AmericanRivers";
					break;
				case "BlueSky":
					appendedText = " @americanrivers.bsky.social";
					break;
				default:
					appendedText = "";
		}
	
		// Append the text
		const finalText = cleanText + appendedText;
	
		// Fallback: Use a temporary textarea
		const textarea = document.createElement("textarea");
		textarea.value = finalText;
		document.body.appendChild(textarea);
		textarea.select();
	
		try {
			document.execCommand("copy"); // Works in older browsers
			showToast(1, "The post text has been copied to your clipboard.", "success");
		} catch (err) {
			console.error("Clipboard copy failed:", err);
			showToast(null, "Failed to copy text. Try manually.", "error");
		}
	
		document.body.removeChild(textarea);
	};
	
	const handleShareClick = (platform, url) => {
		// Close all active toast messages
		toast.dismiss(); 
		
		// Copy text to clipboard
		copyToClipboard(resource.text, platform);
	
		// Download the file
		if (fileDownload) {
			const link = document.createElement("a");
			link.href = fileDownload;
			link.download = "media-file";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
	
			// Step 2
			showToast(2, "The video has been automatically downloaded.", "success");
		}
	
		showToast(3, "Upload the video from your device.", "info");
		showToast(4, "Paste the text: ⌘ / Ctrl + V", "info");
	
		// Step 3, 4, and 5
		setTimeout(() => {
			// Open the social media link in a new tab
			window.open(url, "_blank", "noopener,noreferrer");
		}, 1500);
	};
	
	
	return (
		<Card
			initial="initial"
			animate="animate"
			exit="initial"
			variants={Animation.cards.opacityVariants}
			className="card-river"
		>
			<ImageContainer className="card-river__image-container">
				{resource.image ? (
					<img src={resource.image} alt={`Image for ${resource.title}`} />
				) : (
					<img
						src={`${mainScript.assetPath}images/png/graphics/ar-endangered-placeholder@2x.png`}
						alt="Image placeholder"
						className="placeholder-image"
					/>
				)}
			</ImageContainer>
			<CardContent dangerouslySetInnerHTML={{ __html: resource.text }} />
			<CardShareContainer>
				<CardShareTitle>Share on</CardShareTitle>
				<CardShare className="social-icons">
					<button
						onClick={() => handleShareClick("BlueSky", "https://bsky.app/intent/compose")}
						aria-label="post this on bluesky"
					>
						<div className="bluesky--yellow"></div>
					</button>
					<button
						onClick={() =>
							handleShareClick("Facebook", "https://www.facebook.com/sharer")
						}
						aria-label="post this on facebook"
					>
						<div className="facebook--yellow"></div>
					</button>
					<button
						onClick={() =>
							handleShareClick("Instagram", "https://www.instagram.com")
						}
						aria-label="post this on instagram"
					>
						<div className="instagram--yellow"></div>
					</button>
					<button
						onClick={() => handleShareClick("X", "https://x.com/intent/post?")}
						aria-label="post this on x"
					>
						<div className="twitter--yellow"></div>
					</button>
					<button
						onClick={() =>
							handleShareClick("LinkedIn", "https://www.linkedin.com/shareArticle?mini=true")
						}
						aria-label="post this on linkedin"
					>
						<div className="linkedin--yellow"></div>
					</button>
				</CardShare>
			</CardShareContainer>
		</Card>
	);
};

export default MediaCard;

MediaCard.propTypes = {
	resource: PropTypes.object,
};

// Styled Components
const Card = styled(motion.li)`
    margin: 0;
    display: flex;
	flex-direction: column;
	justify-content: unset;
	gap: 2rem;
`;

const ImageContainer = styled.div`
	padding-top: calc(235 / 409 * 100%);

	> img {
		&:hover {
			transition: unset;
			transform: unset;
		}
	}
`;

const CardContent = styled.div`
	> *:not(:last-child) {
		margin-bottom: 3rem;
	}
	> :last-child {
		margin-bottom: 1rem;
	}

	blockquote {
		margin-left: 0;
		
		> p {
			display: inline;
		}

		&:before {
			content: "“";
		}
		&:after {
			content: "”";
		}
	} 
	
`;

const CardShareContainer = styled.div`
	margin-top: auto;
	padding-top: 3rem;
	display: flex;
	align-items: center;
	gap: 2rem;
	border-top: 1px solid rgba(35, 35, 35, 0.2);
`;

const CardShare = styled.div`
	display: flex;
	gap: 2rem;
	align-items: center;
	margin-top: 0;
	button {
		background: none;
		border: none;
		cursor: pointer;
		padding: 0;

		> * {
			border-radius: unset !important;
		}

		.instagram--yellow {
			width: 3.2rem;
			height: 3.2rem;
		}
	}
`;

const CardShareTitle = styled.p`
	font-size: 1.4rem;
	line-height: 1.6rem;
	letter-spacing: 0.42px;
	text-transform: uppercase;
	font-weight: 700;
`;